/**
 * S4 — Account (`/profile`). One page, three sections: Profile (name editable,
 * email read-only in Martian Mono), Language, Security (change password + MFA
 * enroll/verify/disable). Shared by `client` and `client_salesman` (only the
 * surrounding sidebar differs).
 *
 * Spec: teivor-build-spec/08-ui-spec.md §S4. Backed by the real iam account
 * machinery (`/api/iam/account/*`) — the spec's thin `/api/com/account` proxy
 * is not built, so we bind directly to iam rather than fabricate a proxy.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CodeBlock,
  EmptyState,
  FormField,
  Input,
  InlineAlert,
  PasswordInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
} from '@teivor/shared-ui';
import { ConflictError } from '@teivor/shared-utils/api';

import { PageContainer } from '../../../components/PageContainer';
import {
  useAccountQuery,
  useChangePasswordMutation,
  useDisableMfaMutation,
  useEnrollMfaMutation,
  useUpdateLanguageMutation,
  useUpdateProfileMutation,
  useVerifyMfaMutation,
} from '../queries';
import type { PortalAccount } from '../types';

// ─── Profile ────────────────────────────────────────────────────────────────

function ProfileSection({ account }: { account: PortalAccount }) {
  const { t } = useTranslation();
  const update = useUpdateProfileMutation();
  const [name, setName] = React.useState(account.displayName);

  React.useEffect(() => setName(account.displayName), [account.displayName]);

  const dirty = name.trim() !== account.displayName && name.trim().length > 0;

  const onSave = () => {
    update.mutate(
      { displayName: name.trim(), version: account.version },
      {
        onSuccess: () => toast.success(t('com.account.saved', 'Uloženo / Saved')),
        onError: (err) =>
          toast.error(
            err instanceof ConflictError
              ? t('com.account.conflict', 'Účet se mezitím změnil — načtěte prosím znovu / Account changed — please reload')
              : t('com.account.save_error', 'Uložení se nezdařilo / Could not save'),
          ),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('com.account.profile', 'Profil / Profile')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <FormField label={t('com.account.name', 'Jméno / Name')} htmlFor="acct-name">
          <Input id="acct-name" value={name} onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField label={t('com.account.email', 'E-mail')} htmlFor="acct-email">
          <Input
            id="acct-email"
            value={account.email}
            readOnly
            disabled
            className="tabular-nums"
            style={{ fontFamily: 'var(--tvl-font-mono)' }}
          />
        </FormField>
        <div>
          <Button variant="primary" size="sm" disabled={!dirty} loading={update.isPending} onClick={onSave}>
            {t('common.save', 'Uložit / Save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Language ───────────────────────────────────────────────────────────────

function LanguageSection({ account }: { account: PortalAccount }) {
  const { t, i18n } = useTranslation();
  const update = useUpdateLanguageMutation();
  const current: 'cs-CZ' | 'en-US' = account.preferredLanguage === 'en-US' ? 'en-US' : 'cs-CZ';

  const onChange = (value: string) => {
    const lang = value === 'en-US' ? 'en-US' : 'cs-CZ';
    if (lang === current) return;
    update.mutate(
      { preferredLanguage: lang, version: account.version },
      {
        onSuccess: () => {
          void i18n.changeLanguage(lang === 'en-US' ? 'en' : 'cs');
          toast.success(t('com.account.saved', 'Uloženo / Saved'));
        },
        onError: (err) =>
          toast.error(
            err instanceof ConflictError
              ? t('com.account.conflict', 'Účet se mezitím změnil — načtěte prosím znovu / Account changed — please reload')
              : t('com.account.save_error', 'Uložení se nezdařilo / Could not save'),
          ),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('com.account.language', 'Jazyk / Language')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-w-xs">
          <Select value={current} onValueChange={onChange} aria-label={t('com.account.language', 'Jazyk / Language')}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cs-CZ">Čeština</SelectItem>
              <SelectItem value="en-US">English</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Security: change password ──────────────────────────────────────────────

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

function PasswordSection() {
  const { t } = useTranslation();
  const change = useChangePasswordMutation();
  const form = useForm<PasswordForm>({
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = (values: PasswordForm) => {
    if (values.newPassword !== values.confirmPassword) {
      form.setError('confirmPassword', { message: t('com.account.pw_mismatch', 'Hesla se neshodují / Passwords do not match') });
      return;
    }
    change.mutate(values, {
      onSuccess: () => {
        toast.success(t('com.account.pw_changed', 'Heslo změněno / Password changed'));
        form.reset();
      },
      onError: () => toast.error(t('com.account.pw_error', 'Heslo se nepodařilo změnit / Could not change password')),
    });
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
      <FormField label={t('com.account.current_pw', 'Současné heslo / Current password')} htmlFor="cur-pw" required>
        <PasswordInput id="cur-pw" autoComplete="current-password" {...form.register('currentPassword', { required: true })} />
      </FormField>
      <FormField
        label={t('com.account.new_pw', 'Nové heslo / New password')}
        htmlFor="new-pw"
        required
        helperText={t('com.account.pw_hint', 'Alespoň 12 znaků / At least 12 characters')}
        error={form.formState.errors.newPassword ? t('validation.required', 'Toto pole je povinné / Required') : undefined}
      >
        <PasswordInput id="new-pw" autoComplete="new-password" {...form.register('newPassword', { required: true, minLength: 12 })} />
      </FormField>
      <FormField
        label={t('com.account.confirm_pw', 'Potvrzení hesla / Confirm password')}
        htmlFor="confirm-pw"
        required
        error={form.formState.errors.confirmPassword?.message}
      >
        <PasswordInput id="confirm-pw" autoComplete="new-password" {...form.register('confirmPassword', { required: true })} />
      </FormField>
      <div>
        <Button type="submit" variant="primary" size="sm" loading={change.isPending}>
          {t('com.account.change_pw', 'Změnit heslo / Change password')}
        </Button>
      </div>
    </form>
  );
}

// ─── Security: MFA ──────────────────────────────────────────────────────────

function MfaSection({ account }: { account: PortalAccount }) {
  const { t } = useTranslation();
  const enroll = useEnrollMfaMutation();
  const verify = useVerifyMfaMutation();
  const disable = useDisableMfaMutation();

  const [otpauthUri, setOtpauthUri] = React.useState<string | null>(null);
  const [code, setCode] = React.useState('');
  const [backupCodes, setBackupCodes] = React.useState<string[] | null>(null);
  const [disableCode, setDisableCode] = React.useState('');

  const startEnroll = () =>
    enroll.mutate(undefined, {
      onSuccess: (res) => setOtpauthUri(res.otpauthUri),
      onError: () => toast.error(t('com.account.mfa_error', 'Nastavení MFA se nezdařilo / MFA setup failed')),
    });

  const submitVerify = () =>
    verify.mutate(
      { code },
      {
        onSuccess: (res) => {
          setBackupCodes(res.backupCodes);
          setOtpauthUri(null);
          setCode('');
          toast.success(t('com.account.mfa_on', 'MFA zapnuto / MFA enabled'));
        },
        onError: () => toast.error(t('com.account.mfa_code_error', 'Neplatný kód / Invalid code')),
      },
    );

  const submitDisable = () =>
    disable.mutate(
      { code: disableCode },
      {
        onSuccess: () => {
          setDisableCode('');
          toast.success(t('com.account.mfa_off', 'MFA vypnuto / MFA disabled'));
        },
        onError: () => toast.error(t('com.account.mfa_code_error', 'Neplatný kód / Invalid code')),
      },
    );

  if (account.mfaEnabled) {
    return (
      <div className="flex flex-col gap-3">
        <InlineAlert variant="success">
          {t('com.account.mfa_enabled', 'Vícefaktorové ověření je zapnuté / Multi-factor authentication is enabled')}
        </InlineAlert>
        <FormField label={t('com.account.mfa_disable_code', 'Kód pro vypnutí / Code to disable')} htmlFor="mfa-disable">
          <Input
            id="mfa-disable"
            inputMode="numeric"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            className="tabular-nums tracking-widest"
            style={{ fontFamily: 'var(--tvl-font-mono)' }}
          />
        </FormField>
        <div>
          <Button variant="danger" size="sm" disabled={disableCode.length < 6} loading={disable.isPending} onClick={submitDisable}>
            {t('com.account.mfa_disable', 'Vypnout MFA / Disable MFA')}
          </Button>
        </div>
      </div>
    );
  }

  if (backupCodes) {
    return (
      <div className="flex flex-col gap-3">
        <InlineAlert variant="warning">
          {t('com.account.backup_codes_notice', 'Uložte si tyto záložní kódy na bezpečné místo. Zobrazí se jen jednou. / Save these backup codes somewhere safe. They are shown only once.')}
        </InlineAlert>
        <CodeBlock code={backupCodes.join('\n')} />
      </div>
    );
  }

  if (otpauthUri) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-[14px] leading-[20px] text-[var(--tvl-ink-2)]">
          {t('com.account.mfa_scan', 'Naskenujte tento odkaz v autentizační aplikaci a zadejte kód. / Add this to your authenticator app and enter the code.')}
        </p>
        <CodeBlock code={otpauthUri} />
        <FormField label={t('com.account.mfa_code', 'Ověřovací kód / Verification code')} htmlFor="mfa-code">
          <Input
            id="mfa-code"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="tabular-nums tracking-widest"
            style={{ fontFamily: 'var(--tvl-font-mono)' }}
          />
        </FormField>
        <div>
          <Button variant="primary" size="sm" disabled={code.length !== 6} loading={verify.isPending} onClick={submitVerify}>
            {t('com.account.mfa_verify', 'Ověřit a zapnout / Verify & enable')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[14px] leading-[20px] text-[var(--tvl-ink-2)]">
        {t('com.account.mfa_off_notice', 'Vícefaktorové ověření je vypnuté. / Multi-factor authentication is off.')}
      </p>
      <div>
        <Button variant="secondary" size="sm" loading={enroll.isPending} onClick={startEnroll}>
          {t('com.account.mfa_enroll', 'Zapnout MFA / Enable MFA')}
        </Button>
      </div>
    </div>
  );
}

function SecuritySection({ account }: { account: PortalAccount }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('com.account.security', 'Zabezpečení / Security')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-8">
        <PasswordSection />
        <div className="border-t border-[var(--tvl-hairline)] pt-6">
          <h3 className="mb-3 text-[15px] font-semibold leading-[20px] text-[var(--tvl-ink)]">
            {t('com.account.mfa', 'Vícefaktorové ověření / Multi-factor authentication')}
          </h3>
          <MfaSection account={account} />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function AccountPage() {
  const { t } = useTranslation();
  const { data: account, isLoading, isError, refetch } = useAccountQuery();

  return (
    <PageContainer width="narrow">
      <header>
        <h1 className="text-[22px] font-semibold leading-[28px] text-[var(--tvl-ink)] md:text-[26px]">
          {t('com.account.title', 'Účet / Account')}
        </h1>
      </header>

      {isLoading ? (
        <div className="flex flex-col gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-24" />
          <Skeleton className="h-64" />
        </div>
      ) : isError || !account ? (
        <EmptyState
          variant="error-loading"
          title={t('com.account.error', 'Účet se nepodařilo načíst / Could not load your account')}
          action={
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              {t('common.retry', 'Zkusit znovu / Retry')}
            </Button>
          }
        />
      ) : (
        <>
          <ProfileSection account={account} />
          <LanguageSection account={account} />
          <SecuritySection account={account} />
        </>
      )}
    </PageContainer>
  );
}
